<script lang="ts">
  /**
   * A single REVIEW-tier card (D-09 — 4 facts, ~1s glance read). Roll-your-own
   * Pointer Events swipe (07-RESEARCH Pattern 1): horizontal drag only, commit
   * past SWIPE_THRESHOLD (right=keep, left=exclude), tap under TAP_SLOP opens the
   * relabel palette. The card OWNS horizontal (`touch-action: pan-y`), the browser
   * keeps vertical scroll — the inverse of MetricRings' pan-x. NEVER `touch-action:
   * none` (the WKWebView trap, MetricRings.svelte:215-219).
   */
  import type { Lap } from "./laps";
  import StrokePill from "./StrokePill.svelte";
  import { haptic } from "./haptics";
  import { fmtMSS } from "./format";
  import {
    SWIPE_THRESHOLD,
    TAP_SLOP,
    classifySwipe,
  } from "./swipeDeckLogic";

  interface Props {
    lap: Lap;
    /** The flagged-metric line, e.g. "SWOLF 140 · 2SD slow" (D-09 fact 3). */
    flagLine: string;
    onCommit: (dir: "keep" | "exclude") => void;
    onRelabel: () => void;
  }
  let { lap, flagLine, onCommit, onRelabel }: Props = $props();

  let dx = $state(0);
  let dragging = $state(false);
  let startX = 0;

  function down(e: PointerEvent) {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragging = true;
    startX = e.clientX;
    dx = 0;
  }
  function move(e: PointerEvent) {
    if (!dragging) return;
    dx = e.clientX - startX; // horizontal ONLY — ignore clientY entirely (D-07)
  }
  function up() {
    dragging = false;
    const outcome = classifySwipe(dx);
    dx = 0; // snap-back transition fires via the CSS below
    if (outcome === "tap") {
      onRelabel();
      return;
    }
    if (outcome === "keep" || outcome === "exclude") {
      haptic("selection"); // commit haptic (D-08) — runtime-only, verified on device
      onCommit(outcome);
    }
    // "snap-back" → no commit; dx already reset
  }

  // Subtle keep/exclude direction cue: right tints toward accent-primary (keep),
  // left mutes toward text-muted (quarantine, NEVER red — D-16).
  let pastThreshold = $derived(Math.abs(dx) >= SWIPE_THRESHOLD);
  let hintKeep = $derived(dx > TAP_SLOP);
  let hintExclude = $derived(dx < -TAP_SLOP);
</script>

<div
  class="card"
  class:hint-keep={hintKeep}
  class:hint-exclude={hintExclude}
  class:armed={pastThreshold}
  style:transform={`translateX(${dx}px) rotate(${dx * 0.04}deg)`}
  style:transition={dragging ? "none" : "transform 0.18s ease-out"}
  onpointerdown={down}
  onpointermove={move}
  onpointerup={up}
  onpointercancel={up}
  role="button"
  tabindex="0"
  aria-label={`Lap ${lap.index}: ${flagLine}. Swipe right to keep, left to exclude, tap to relabel.`}
>
  <div class="lapnum t-numeric-lg">L{lap.index}</div>
  <div class="flag t-subtitle">{flagLine}</div>
  <div class="ctx t-body">
    <StrokePill stroke={lap.strokeStyle} />
    <span class="dur">{fmtMSS(lap.durationSeconds)}</span>
  </div>
  <div class="cues t-caption" aria-hidden="true">
    <span class="cue cue-exclude">← Exclude</span>
    <span class="cue cue-relabel">tap · Relabel</span>
    <span class="cue cue-keep">Keep →</span>
  </div>
</div>

<style>
  .card {
    /* pan-y: browser keeps VERTICAL scroll, the card owns HORIZONTAL.
       Inverse of MetricRings.svelte:218 (pan-x). The WKWebView trap is
       disabling touch entirely — never do that on a gesture surface. */
    touch-action: pan-y;
    overscroll-behavior: contain;
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    padding: var(--card-pad, var(--space-6));
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    cursor: grab;
    will-change: transform;
  }
  .card.armed {
    border-color: var(--text-muted);
  }
  .card.hint-keep {
    box-shadow: inset 4px 0 0 var(--accent-primary);
  }
  .card.hint-exclude {
    /* Exclude direction cue is a calm grey mute, never alarm-red (D-16). */
    box-shadow: inset -4px 0 0 var(--text-muted);
  }

  .lapnum {
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }
  .flag {
    color: var(--text-primary);
  }
  .ctx {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    color: var(--text-muted);
  }
  .dur {
    font-variant-numeric: tabular-nums;
  }

  .cues {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--space-2);
    color: var(--text-dim);
    text-transform: uppercase;
  }
  .cue-keep {
    color: var(--accent-primary);
  }
  .cue-exclude {
    color: var(--text-muted);
  }
</style>
